"use client";

import { useState, useTransition } from "react";
import { LogIn, Eye, EyeOff, Mail, Lock, Loader2 } from "lucide-react";
import { loginAction } from "@/lib/actions";
import type { Messages } from "@/lib/i18n";

export function LoginForm({
  messages,
  fallbackError,
}: {
  messages: Messages["login"];
  fallbackError: string;
}) {
  const [error, setError] = useState<string | null>(null);
  const [showPw, setShowPw] = useState(false);
  const [pending, startTransition] = useTransition();

  const onSubmit = (formData: FormData) => {
    setError(null);
    const email = String(formData.get("email") ?? "").trim();
    const password = String(formData.get("password") ?? "");
    // Client guard miesto nativnej validacie: browserova bublina nie je lokalizovana
    // a navyse misfiruje pri autofille (hodnota je v poli, ale validity je stale).
    // FormData pri submite autofill hodnotu obsahuje, takze tu ju spolahlivo overime.
    if (!email || !password) {
      setError(messages.fillField);
      return;
    }
    startTransition(async () => {
      const res = await loginAction(formData);
      // Pri uspechu server action presmeruje - sem sa navratova hodnota nedostane.
      // Pri zlyhani pride ok:false a zobrazime chybu.
      if (res && !res.ok) setError(res.error ?? fallbackError);
    });
  };

  return (
    <form action={onSubmit} noValidate className="w-full">
      <div className="lf-field">
        <label className="lf-label" htmlFor="lf-email">{messages.email}</label>
        <div className="lf-inwrap">
          <Mail className="lf-ic" strokeWidth={2} />
          <input
            id="lf-email"
            autoFocus
            required
            type="email"
            name="email"
            placeholder={messages.emailPlaceholder}
            autoComplete="email"
            className="lf-input"
          />
        </div>
      </div>

      <div className="lf-field">
        <label className="lf-label" htmlFor="lf-password">{messages.password}</label>
        <div className="lf-inwrap">
          <Lock className="lf-ic" strokeWidth={2} />
          <input
            id="lf-password"
            required
            type={showPw ? "text" : "password"}
            name="password"
            placeholder="••••••••"
            autoComplete="current-password"
            className="lf-input"
          />
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            aria-label={showPw ? messages.hidePassword : messages.showPassword}
            title={showPw ? messages.hidePassword : messages.showPassword}
            className="lf-eye"
          >
            {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        </div>
      </div>

      <div className="lf-forgot">
        <a href="/forgot">{messages.forgotLink}</a>
      </div>

      {error && <p className="lf-error">{error}</p>}

      <button type="submit" disabled={pending} className="lf-submit">
        {pending ? <Loader2 className="size-4 animate-spin" /> : <LogIn className="size-4" strokeWidth={2.2} />}
        {pending ? messages.signingIn : messages.signIn}
      </button>
    </form>
  );
}
